import React, { useMemo, useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronRight,
  Building2,
  UserCircle,
  MapPin,
} from "lucide-react";

export default function LeadPacingTab({
  vars: defaults = {
    totalLeads: 440,
    phase1Leads: 150,
    phase1Start: "2025-12-08",
    phase1Weeks: 6,
    phase2Start: "2026-01-19",
    phase2Weeks: 6,
    regionSplit: { SEA: 220, INDIA: 220 },
    industryTargets: { FinServ: 220, Tech: 220 },
  },
  onChange,
}) {
  const [vars, setVars] = useState(defaults);
  const [actuals, setActuals] = useState([]);
  const [expandedDate, setExpandedDate] = useState(null); // Track which date is expanded

  useEffect(() => {
    fetch("/APAC_Reporting_India_SEA.csv")
      .then(async (res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const rows = [];
        let cells = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const next = text[i + 1];
          if (char === '"') {
            if (inQuotes && next === '"') {
              current += '"';
              i++;
            } else inQuotes = !inQuotes;
          } else if (!inQuotes && char === ",") {
            cells.push(current.trim());
            current = "";
          } else if (
            !inQuotes &&
            (char === "\n" || (char === "\r" && next === "\n"))
          ) {
            if (char === "\r") i++;
            cells.push(current.trim());
            rows.push(cells);
            cells = [];
            current = "";
          } else current += char;
        }
        if (current || cells.length > 0) {
          cells.push(current.trim());
          rows.push(cells);
        }

        // Capture Title (0), Country (1), Company (5), and Date (9)
        const validLeads = rows
          .slice(1)
          .map((row) => ({
            Title: row[0],
            Country: row[1],
            Company: row[5],
            Date: row[9],
          }))
          .filter((lead) => lead.Date && lead.Date.includes("/"));

        setActuals(validLeads);
      });
  }, []);

  const parseMDY = (s) => {
    const m = String(s)
      .trim()
      .match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (!m) return null;
    let [, mm, dd, yy] = m;
    const year = yy.length === 2 ? 2000 + Number(yy) : Number(yy);
    return new Date(year, Number(mm) - 1, Number(dd));
  };

  const updateVar = (k, v) => {
    const next = { ...vars, [k]: v };
    setVars(next);
    onChange && onChange(next);
  };

  // Group actuals by date and keep lead details
  const deliveryData = useMemo(() => {
    const grouped = {};
    actuals.forEach((lead) => {
      if (!grouped[lead.Date]) grouped[lead.Date] = [];
      grouped[lead.Date].push(lead);
    });
    return Object.entries(grouped).sort(
      (a, b) => parseMDY(a[0]) - parseMDY(b[0])
    );
  }, [actuals]);

  // Pacing Logic (Goal/Actual calculations)
  const { weeks, cumulativeGoal, cumulativeActual } = useMemo(() => {
    const toDate = (s) => new Date(s + "T00:00:00");
    const addDays = (d, n) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
    const spreadEven = (total, buckets) => {
      const base = Math.floor(total / Math.max(1, buckets));
      const rem = Math.max(0, total - base * Math.max(1, buckets));
      return Array.from(
        { length: Math.max(1, buckets) },
        (_, i) => base + (i < rem ? 1 : 0)
      );
    };

    const w = [];
    const p1Count = spreadEven(vars.phase1Leads, vars.phase1Weeks);
    const p2Count = spreadEven(
      Math.max(0, vars.totalLeads - vars.phase1Leads),
      vars.phase2Weeks
    );
    const s1 = toDate(vars.phase1Start);
    for (let i = 0; i < vars.phase1Weeks; i++) {
      const mon = addDays(s1, i * 7);
      w.push({ monday: mon, end: addDays(mon, 6), new: p1Count[i] });
    }
    const s2 = toDate(vars.phase2Start);
    for (let i = 0; i < vars.phase2Weeks; i++) {
      const mon = addDays(s2, i * 7);
      w.push({ monday: mon, end: addDays(mon, 6), new: p2Count[i] });
    }

    let gSum = 0,
      aSum = 0;
    const cGoal = w.map((week) => (gSum += week.new));
    const aBuckets = new Array(w.length).fill(0);
    actuals.forEach((lead) => {
      const lDate = parseMDY(lead.Date);
      const idx = w.findIndex((wk) => lDate >= wk.monday && lDate <= wk.end);
      if (idx !== -1) aBuckets[idx] += 1;
    });
    return {
      weeks: w,
      cumulativeGoal: cGoal,
      cumulativeActual: aBuckets.map((count) => (aSum += count)),
    };
  }, [vars, actuals]);

  // Chart Consts
  const H = 240,
    W = 640,
    pad = { t: 16, r: 10, b: 46, l: 38 },
    innerW = W - pad.l - pad.r,
    innerH = H - pad.t - pad.b;
  const x = (i) => pad.l + (innerW * i) / Math.max(1, weeks.length - 1);
  const y = (v) =>
    pad.t +
    innerH -
    (innerH * v) /
      Math.max(
        1,
        Math.max(vars.totalLeads, ...cumulativeGoal, ...cumulativeActual)
      );

  return (
    <section className="card lg section">
      <div className="chips" style={{ marginBottom: 10 }}>
        <span className="pill">Pacing</span>
        <span className="pill green">Verified: {actuals.length}</span>
      </div>

      <h2 className="h2">Lead Pacing & Qualitative Drill-Down</h2>

      {/* Grid and SVG sections remain same as before... */}
      <div className="grid md-g-2" style={{ marginBottom: 16 }}>
        <div className="card">
          <h3 className="h2">Campaign Progress</h3>
          <div className="kpi">
            <div className="val" style={{ color: "#10b981" }}>
              {actuals.length}
            </div>
            <div className="label">Total Leads</div>
          </div>
        </div>
        <div className="card">
          <h3 className="h2">Calendar</h3>
          <input
            className="input"
            type="date"
            value={vars.phase1Start}
            onChange={(e) => updateVar("phase1Start", e.target.value)}
          />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="h2">Pacing Curve</h3>
        <svg width={W} height={H} style={{ overflow: "visible" }}>
          {/* SVG Path Logic here */}
          <line
            x1={pad.l}
            y1={pad.t + innerH}
            x2={pad.l + innerW}
            y2={pad.t + innerH}
            stroke="#cbd5e1"
          />
          {cumulativeGoal.map(
            (v, i) =>
              i > 0 && (
                <line
                  key={`g-${i}`}
                  x1={x(i - 1)}
                  y1={y(cumulativeGoal[i - 1])}
                  x2={x(i)}
                  y2={y(v)}
                  stroke="#0B1A3C"
                  strokeWidth="2"
                />
              )
          )}
          {cumulativeActual.map(
            (v, i) =>
              i > 0 && (
                <line
                  key={`a-${i}`}
                  x1={x(i - 1)}
                  y1={y(cumulativeActual[i - 1])}
                  x2={x(i)}
                  y2={y(v)}
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeDasharray="4"
                />
              )
          )}
        </svg>
      </div>

      {/* NEW: Enhanced Delivery Breakdown with Drill-Down */}
      <div className="card" style={{ marginTop: 12 }}>
        <h3 className="h2">Daily Delivery Log</h3>
        <div className="table-wrap">
          <table
            className="tbl"
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: "0 4px",
            }}
          >
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={{ padding: "12px", textAlign: "left" }}>
                  Date Delivered
                </th>
                <th style={{ padding: "12px", textAlign: "right" }}>Leads</th>
                <th style={{ width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {deliveryData.map(([date, leads]) => (
                <React.Fragment key={date}>
                  <tr
                    onClick={() =>
                      setExpandedDate(expandedDate === date ? null : date)
                    }
                    style={{
                      cursor: "pointer",
                      background:
                        expandedDate === date ? "#f1f5f9" : "transparent",
                    }}
                  >
                    <td
                      className="sm"
                      style={{ padding: "12px", fontWeight: 600 }}
                    >
                      {date}
                    </td>
                    <td
                      className="sm"
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      {leads.length}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {expandedDate === date ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </td>
                  </tr>
                  {expandedDate === date && (
                    <tr>
                      <td
                        colSpan="3"
                        style={{
                          padding: "0 12px 12px 12px",
                          background: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: "8px",
                            paddingTop: "8px",
                          }}
                        >
                          {leads.map((l, idx) => (
                            <div
                              key={idx}
                              style={{
                                padding: "10px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontWeight: 700,
                                  fontSize: "13px",
                                  marginBottom: "4px",
                                }}
                              >
                                <Building2 size={14} color="#64748b" />{" "}
                                {l.Company}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: "11px",
                                  color: "#475569",
                                }}
                              >
                                <UserCircle size={14} color="#94a3b8" />{" "}
                                {l.Title}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: "11px",
                                  color: "#94a3b8",
                                  marginTop: "2px",
                                }}
                              >
                                <MapPin size={12} /> {l.Country}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
